import React, { useState } from 'react';
import { CheckCircle2, Loader2, ChevronDown, ChevronUp, Layers } from 'lucide-react';

const AGENT_META = {
  SupervisorAgent: {
    icon: '🧠',
    name: 'Supervisor / Planner',
    role: 'Decomposing objectives & creating execution DAG',
  },
  SolutionsArchitectAgent: {
    icon: '🏗️',
    name: 'Solutions Architect',
    role: 'Designing cloud, microservices & RAG pipelines',
  },
  PricingEstimatorAgent: {
    icon: '⏱️',
    name: 'Pricing & Estimator',
    role: 'Structuring sprint phases & engagement models',
  },
  CriticReviewerAgent: {
    icon: '🛡️',
    name: 'Critic & Compliance',
    role: 'Validating zero-fixed-rupee, NDA & feasibility',
  },
  ExecutiveSynthesizerAgent: {
    icon: '📄',
    name: 'Executive Synthesizer',
    role: 'Finalizing proposal & registering MySQL ticket',
  },
};

export function MultiAgentTracker({ events = [], workflow = null }) {
  const [isExpanded, setIsExpanded] = useState(true);

  if (!events || events.length === 0) {
    if (!workflow) return null;
  }

  // Derive latest status for each agent
  const agentStates = {
    SupervisorAgent: { status: 'PENDING', message: 'Awaiting task queue...' },
    SolutionsArchitectAgent: { status: 'PENDING', message: 'Awaiting architectural specs...' },
    PricingEstimatorAgent: { status: 'PENDING', message: 'Awaiting commercial parameters...' },
    CriticReviewerAgent: { status: 'PENDING', message: 'Awaiting drafted artifacts...' },
    ExecutiveSynthesizerAgent: { status: 'PENDING', message: 'Awaiting audit clearance...' },
  };

  events.forEach((evt) => {
    if (evt.agent && agentStates[evt.agent]) {
      agentStates[evt.agent] = {
        status: evt.status,
        message: evt.message || agentStates[evt.agent].message,
      };
    }
  });

  const isAllDone = Object.values(agentStates).every((s) => s.status === 'DONE') || Boolean(workflow);

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95))',
        border: '1px solid rgba(99, 102, 241, 0.3)',
        borderRadius: '12px',
        padding: '12px 16px',
        marginBottom: '12px',
        color: '#f8fafc',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
        fontSize: '13px',
      }}
    >
      {/* Header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              background: 'rgba(99, 102, 241, 0.2)',
              borderRadius: '6px',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Layers size={15} color="#818cf8" />
          </div>
          <div>
            <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>Level 3 Multi-Agent State Graph</span>
              {isAllDone ? (
                <span
                  style={{
                    fontSize: '11px',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    background: 'rgba(16, 185, 129, 0.2)',
                    color: '#34d399',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                  }}
                >
                  Completed (5 Subagents)
                </span>
              ) : (
                <span
                  style={{
                    fontSize: '11px',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    background: 'rgba(99, 102, 241, 0.2)',
                    color: '#a5b4fc',
                    border: '1px solid rgba(99, 102, 241, 0.3)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Loader2 size={10} className="spin-slow" /> Orchestrating
                </span>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {workflow?.criticScore && (
            <span
              style={{
                fontSize: '11px',
                padding: '2px 8px',
                borderRadius: '6px',
                background: 'rgba(59, 130, 246, 0.2)',
                color: '#60a5fa',
                border: '1px solid rgba(59, 130, 246, 0.3)',
              }}
            >
              Audit: {workflow.criticScore}/10 ✅
            </span>
          )}
          {isExpanded ? <ChevronUp size={16} color="#94a3b8" /> : <ChevronDown size={16} color="#94a3b8" />}
        </div>
      </div>

      {/* Expanded Step Tracker */}
      {isExpanded && (
        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {Object.entries(AGENT_META).map(([key, meta], idx) => {
            const state = agentStates[key];
            const isRunning = state.status === 'RUNNING';
            const isDone = state.status === 'DONE';

            return (
              <div
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  padding: '6px 10px',
                  borderRadius: '8px',
                  background: isRunning
                    ? 'rgba(99, 102, 241, 0.12)'
                    : isDone
                    ? 'rgba(16, 185, 129, 0.06)'
                    : 'rgba(255, 255, 255, 0.02)',
                  border: isRunning
                    ? '1px solid rgba(99, 102, 241, 0.4)'
                    : '1px solid transparent',
                  transition: 'all 0.3s ease',
                }}
              >
                <div style={{ fontSize: '15px', marginTop: '1px' }}>{meta.icon}</div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 600, color: isDone ? '#e2e8f0' : isRunning ? '#a5b4fc' : '#64748b' }}>
                      {meta.name}
                    </span>
                    <span style={{ fontSize: '11px' }}>
                      {isRunning && (
                        <span style={{ color: '#818cf8', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <Loader2 size={11} className="spin-slow" /> Running
                        </span>
                      )}
                      {isDone && (
                        <span style={{ color: '#34d399', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                          <CheckCircle2 size={12} /> Done
                        </span>
                      )}
                      {!isRunning && !isDone && (
                        <span style={{ color: '#475569' }}>Queued</span>
                      )}
                    </span>
                  </div>

                  <p
                    style={{
                      margin: '2px 0 0 0',
                      fontSize: '11.5px',
                      color: isRunning ? '#cbd5e1' : isDone ? '#94a3b8' : '#475569',
                      lineHeight: 1.4,
                      wordBreak: 'break-word',
                    }}
                  >
                    {state.message}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
